import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { companyService } from '../../services/companyService';
import { useAuth } from '../../contexts/AuthContext';

const CompanyCreate = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setActiveCompany } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [domain, setDomain] = useState('');
  const [vtexAccountName, setVtexAccountName] = useState('');
  const [companyNameError, setCompanyNameError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCompanyNameError('');
    setSubmitError('');

    if (!companyName.trim()) {
      setCompanyNameError(t('companyCreate.errors.nameRequired'));
      return;
    }

    setIsSubmitting(true);
    try {
      const newCompany = await companyService.createCompany({
        companyName: companyName.trim(),
        ...(domain.trim() ? { domain: domain.trim() } : {}),
        ...(vtexAccountName.trim() ? { vtexAccountName: vtexAccountName.trim() } : {})
      });
      setActiveCompany(newCompany.id);
      navigate('/dashboard');
    } catch (error) {
      if (error.code === 'DUPLICATE_NAME') {
        setCompanyNameError(t('companyCreate.errors.nameDuplicate'));
      } else {
        setSubmitError(t('companyCreate.errors.genericError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
      <div className={`transition-all duration-250 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-[280px]'}`}>
        <div className="sticky top-0 z-10 bg-background border-b border-border px-4 md:px-6 lg:px-8 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            ← {t('common.back')}
          </button>
          <h1 className="text-xl font-semibold text-foreground">{t('companyCreate.title')}</h1>
        </div>

        <div className="p-4 md:p-6 lg:p-8 max-w-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Input
                label={t('companyCreate.fields.companyName')}
                type="text"
                value={companyName}
                onChange={(e) => {
                  setCompanyName(e.target.value);
                  setCompanyNameError('');
                }}
                error={companyNameError}
                placeholder={t('companyCreate.fields.companyNamePlaceholder')}
                required
              />
            </div>

            <div>
              <Input
                label={t('companyCreate.fields.domain')}
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder={t('companyCreate.fields.domainPlaceholder')}
              />
            </div>

            <div>
              <Input
                label={t('companyCreate.fields.vtexAccountName')}
                type="text"
                value={vtexAccountName}
                onChange={(e) => setVtexAccountName(e.target.value)}
                placeholder={t('companyCreate.fields.vtexAccountNamePlaceholder')}
              />
            </div>

            {submitError && (
              <p className="text-sm text-destructive">{submitError}</p>
            )}

            <Button
              type="submit"
              variant="default"
              loading={isSubmitting}
              className="w-full"
            >
              {t('companyCreate.submit')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompanyCreate;
